/**
 * The dashboard's device charts: when the fleet last checked in, what it runs, who it belongs to,
 * which agent build it is on, how much memory it has, and whether it is ready for Windows 11.
 *
 * Two columns on a desktop, one on a phone. The columns are rebuilt only when the count changes, on
 * the card's own resize, because a split row never stacks two filling children by itself.
 *
 * DRILL-DOWN. Clicking a bar lists the devices behind its number directly under that chart, each one
 * opening its device page; clicking the bar again, or Close, folds it. One list is open at a time,
 * and it survives the dashboard's minute refresh by its row label - it closes only when that row has
 * gone or counts nothing.
 */
import { insets, type Control, type StackPanel } from 'webwin';
import { ManagementChannel, type DeviceDto, type DevicePresenceDto } from '../../contracts.gen.js';
import type { Card } from '../../ui/Card.js';
import { osName, type BarRow, type DeviceAnalytics } from '../../deviceAnalytics.js';
import { ago, join, localDateTime, plural } from '../../format.js';
import { BarChart, LegendSwatch, type BarSeries } from '../../ui/BarChart.js';
import { CardList } from '../../ui/CardList.js';
import { dot } from '../../ui/Dot.js';
import { link } from '../../ui/Link.js';
import {
  buttonRow,
  card,
  fillWith,
  FONT,
  GAP,
  h2,
  hstack,
  muted,
  relayout,
  splitRow,
  stackOf,
  value,
  vstack
} from '../../ui/layout.js';

/** Below this card width the charts stack one per line. */
const TWO_COLUMNS_FROM = 640;
/** The most devices one drill-down lists; past it the list says how many more there are. */
const LIST_MAX = 50;

/** What the drill-down needs beyond the counts: the devices themselves, by id. */
export interface FleetLookup {
  readonly devices: ReadonlyMap<number, DeviceDto>;
  readonly presence: ReadonlyMap<number, DevicePresenceDto>;
  readonly customerNames: ReadonlyMap<number, string>;
}

interface ChartBlock {
  readonly panel: StackPanel;
  readonly rows: readonly BarRow[];
  set(rows: readonly BarRow[], caption: string): void;
  /** Lists the row's devices under the chart, or folds the list when `row` is null. */
  open(row: BarRow | null, fleet: FleetLookup): void;
}

interface ChartBlockOptions {
  readonly onRowClick: (row: BarRow) => void;
  readonly onClose: () => void;
  readonly go: (path: string) => void;
}

export interface DeviceAnalyticsSection {
  readonly panel: Card;
  update(a: DeviceAnalytics, fleet: FleetLookup): void;
}

export function deviceAnalyticsSection(go: (path: string) => void): DeviceAnalyticsSection {
  const single: BarSeries[] = [{ label: 'Devices', color: 'brand' }];
  let fleet: FleetLookup = { devices: new Map(), presence: new Map(), customerNames: new Map() };
  let opened: { block: ChartBlock; label: string } | null = null;

  function toggle(block: ChartBlock, row: BarRow): void {
    const same = opened?.block === block && opened.label === row.label;
    opened?.block.open(null, fleet);
    opened = same ? null : { block, label: row.label };
    if (!same) block.open(row, fleet);
    relayout(grid);
  }

  function close(block: ChartBlock): void {
    block.open(null, fleet);
    if (opened?.block === block) opened = null;
    relayout(grid);
  }

  function make(title: string, series: readonly BarSeries[]): ChartBlock {
    const block: ChartBlock = chartBlock(title, series, {
      onRowClick: row => toggle(block, row),
      onClose: () => close(block),
      go
    });
    return block;
  }

  const checkIn = make('Last check-in', single);
  const os = make('Operating system', single);
  const customers = make('Devices by customer', [
    { label: 'Online', color: 'ok' },
    { label: 'Offline', color: 'mutedText' }
  ]);
  const agents = make('Agent version', single);
  const memory = make('Installed memory', single);
  const tpm = make('Windows 11 TPM readiness', single);
  const blocks = [checkIn, customers, os, agents, memory, tpm];

  const intro = muted('');
  const grid = vstack({ spacing: GAP.lg });
  const shell = card();
  shell.controls.add(stackOf(vstack({ spacing: GAP.md }), h2('Device analytics'), intro, grid));

  let columns = 0;
  function arrange(): void {
    const next = shell.width >= TWO_COLUMNS_FROM ? 2 : 1;
    if (next === columns) return;
    columns = next;
    grid.controls.clear();
    for (let i = 0; i < blocks.length; i += columns) {
      if (columns === 1) {
        grid.controls.add(blocks[i]!.panel);
        continue;
      }
      const row = splitRow({ align: 'start', spacing: GAP.xl, stackBelow: 0 });
      for (const block of blocks.slice(i, i + columns)) fillWith(row, block.panel);
      grid.controls.add(row);
    }
    // Not inside the resize that asked for it: a layout cannot re-enter itself.
    queueMicrotask(() => relayout(grid));
  }
  shell.on('resize', arrange);

  return {
    panel: shell,
    update(a, lookup) {
      fleet = lookup;
      intro.text =
        a.total === 0
          ? 'No managed devices yet.'
          : `${plural(a.total, 'managed device')}, ${a.online} online. Hover a bar for the numbers; click it to list the devices.`;
      checkIn.set(a.checkIn, 'Online now, or when each device last reported in');
      os.set(a.os, '');
      customers.set(a.customers, 'Click a customer to list its devices');
      agents.set(a.agents, 'Newest first');
      memory.set(a.memory, '');
      tpm.set(
        a.tpm,
        a.windowsCount === 0
          ? 'No Windows devices'
          : `${plural(a.windowsCount, 'Windows device')}. "Not reported" means the agent could not read the TPM.`
      );
      if (opened) {
        const { block, label } = opened;
        const row = block.rows.find(r => r.label === label && r.deviceIds.length > 0) ?? null;
        block.open(row, fleet);
        if (!row) opened = null;
      }
      arrange();
      relayout(grid);
    }
  };
}

function chartBlock(title: string, series: readonly BarSeries[], options: ChartBlockOptions): ChartBlock {
  const caption = muted('');
  const empty = muted('Nothing to show yet.');
  const chart = new BarChart({ series, onRowClick: options.onRowClick });
  const drill = drillDown(options);

  const panel = stackOf(vstack({ spacing: GAP.xs }), value(title, { font: FONT.bold }), caption);
  // A legend whenever there is more than one series, so which colour is which is never a guess.
  if (series.length > 1) {
    const legend = buttonRow({ spacing: GAP.md, align: 'center' });
    for (const s of series) legend.controls.add(legendEntry(s));
    panel.controls.add(legend);
  }
  panel.controls.add(chart);
  panel.controls.add(empty);
  panel.controls.add(drill.panel);

  return {
    panel,
    get rows() {
      return chart.rows;
    },
    open(row, fleet) {
      chart.selected = row?.label ?? null;
      drill.show(row, fleet);
    },
    set(rows, text) {
      chart.rows = rows;
      const hasData = rows.some(r => r.values.some(v => v > 0));
      chart.visible = hasData;
      empty.visible = !hasData;
      caption.text = text;
      caption.visible = text.length > 0;
    }
  };
}

interface DrillDown {
  readonly panel: StackPanel;
  show(row: BarRow | null, fleet: FleetLookup): void;
}

/** The devices behind one bar: a heading with the count and its links, then one small card each. */
function drillDown(options: ChartBlockOptions): DrillDown {
  const heading = value('', { font: FONT.bold });
  const links = buttonRow({ spacing: GAP.md, autoWidth: true });
  const head = splitRow({ spacing: GAP.md, align: 'center' });
  fillWith(head, heading);
  head.controls.add(links);
  const more = muted('');
  let fleet: FleetLookup | null = null;

  const list = new CardList<DeviceDto>({
    key: d => d.id,
    version: d => {
      const p = fleet?.presence.get(d.id) ?? d.presence;
      return `${d.name}|${d.customerId}|${d.os?.caption}|${p?.isOnline}|${p?.lastSeenUtc}|${d.lastInventoryUtc}`;
    },
    build: d => deviceCard(d, fleet!, options.go),
    max: LIST_MAX,
    spacing: GAP.xs
  });

  const panel = stackOf(vstack({ spacing: GAP.sm, padding: insets(0, GAP.sm, 0, 0) }), head, list.panel, more);
  panel.visible = false;

  return {
    panel,
    show(row, lookup) {
      fleet = lookup;
      if (!row) {
        panel.visible = false;
        list.clear();
        return;
      }
      const devices = row.deviceIds
        .map(id => lookup.devices.get(id))
        .filter((d): d is DeviceDto => d !== undefined)
        .sort((a, b) => a.name.localeCompare(b.name));
      heading.text = `${row.label}: ${plural(devices.length, 'device')}`;
      links.controls.clear();
      const path = row.path;
      if (path) links.controls.add(link('Open customer →', () => options.go(path)));
      links.controls.add(link('Close', options.onClose));
      list.set(devices);
      more.text = list.overflow > 0 ? `and ${list.overflow} more on the Devices page` : '';
      more.visible = list.overflow > 0;
      panel.visible = true;
    }
  };
}

function deviceCard(d: DeviceDto, fleet: FleetLookup, go: (path: string) => void): Card {
  const p = fleet.presence.get(d.id) ?? d.presence;
  // A phone has no connection to be online on; when it last reported is the fact.
  const phone = d.channel === ManagementChannel.Android;
  const online = !phone && (p?.isOnline ?? false);
  const seen = p?.lastSeenUtc ?? d.lastInventoryUtc;
  const reported = d.mobile?.lastStatusReportUtc;
  const status = phone
    ? reported
      ? `Checked in ${localDateTime(reported)}`
      : 'Not checked in'
    : online
      ? 'Online'
      : seen
        ? `Seen ${ago(seen)}`
        : 'Never seen';

  const row = splitRow({ spacing: GAP.md, align: 'center' });
  row.controls.add(dot(online));
  fillWith(
    row,
    stackOf(
      vstack({ spacing: 0 }),
      value(d.name, { font: FONT.bold }),
      muted(join([fleet.customerNames.get(d.customerId), d.os?.caption ? osName(d.os.caption) : null]))
    )
  );
  row.controls.add(value(status, { font: FONT.small, role: 'muted', wrap: false }));

  const shell = card({ tint: 'alt', bordered: false, padding: insets(10), cursor: 'pointer' });
  shell.controls.add(row);
  shell.onActivate(() => go(`/devices/${d.id}`));
  return shell;
}

function legendEntry(s: BarSeries): Control {
  return stackOf(
    hstack({ spacing: GAP.xs, autoWidth: true, align: 'center' }),
    new LegendSwatch(s.color),
    value(s.label, { font: FONT.small, role: 'muted', wrap: false })
  );
}
